import React, { useEffect, useRef, useState } from 'react'
import { useNavigate, useLocation } from 'react-router-dom'
import { useSelector } from 'react-redux'
import './Sidebar.css'
import { getProducts } from '../utils/api'

const Navbar = () => {
    const state = useSelector(state => state.handleCart)
    const navigate = useNavigate();
    // Safely resolve brand name: only allow short, clean names
    const rawName = (typeof window !== 'undefined') ? localStorage.getItem('displayName') : null;
    const isValidName = (name) => /^[a-zA-Z0-9 &-]{2,24}$/.test(name || '');
    const brandName = isValidName(rawName) ? rawName : 'PrimeBrothers';

    const go = (to) => navigate(to);

    // Theme toggle (light/dark) with persistence
    const [dark, setDark] = useState(false);
    useEffect(() => {
        const saved = localStorage.getItem('themeDark') === '1';
        setDark(saved);
        document.body.classList.toggle('theme-dark', saved);
    }, []);
    const toggleTheme = () => {
        const next = !dark;
        setDark(next);
        document.body.classList.toggle('theme-dark', next);
        localStorage.setItem('themeDark', next ? '1' : '0');
    };
    
    // Search bar state synced with URL query
    const [q, setQ] = useState('');
    const [suggestSrc, setSuggestSrc] = useState([]); // array of strings (titles + categories)
    const [suggest, setSuggest] = useState([]); // filtered suggestions
    const [suggestOpen, setSuggestOpen] = useState(false);
    const [activeIdx, setActiveIdx] = useState(-1);
    const inputRef = useRef(null);
    const location = useLocation();
    useEffect(() => {
        const params = new URLSearchParams(location.search);
        const searchQ = (params.get('q') || '').trim();
        setQ(searchQ);
    }, [location.search]);
    // Prefetch products for suggestions (titles + categories)
    useEffect(() => {
        let mounted = true;
        (async () => {
            try {
                const list = await getProducts();
                if (!mounted) return;
                const titles = list.map(p => String(p.title || '')).filter(Boolean);
                const cats = Array.from(new Set(list.map(p => String(p.category || '')).filter(Boolean)));
                const base = Array.from(new Set([...titles, ...cats]));
                setSuggestSrc(base);
            } catch {}
        })();
        return () => { mounted = false };
    }, []);
    // Build suggestions when q changes
    useEffect(() => {
        const term = q.trim().toLowerCase();
        if (!term) {
            setSuggest([]);
            setActiveIdx(-1);
            return;
        }
        const max = 8;
        const starts = [];
        const contains = [];
        for (const s of suggestSrc) {
            const l = s.toLowerCase();
            if (l.startsWith(term)) starts.push(s);
            else if (l.includes(term)) contains.push(s);
            if (starts.length >= max) break;
        }
        const merged = [...starts, ...contains.filter(s => !starts.includes(s))].slice(0, max);
        setSuggest(merged);
        setSuggestOpen(merged.length > 0);
        setActiveIdx(-1);
    }, [q, suggestSrc]);
    const onSearchSubmit = (e) => {
        e.preventDefault();
        const trimmed = (q || '').trim();
        const to = trimmed ? `/product?q=${encodeURIComponent(trimmed)}` : '/product';
        navigate(to);
    };
    const onPickSuggestion = (value) => {
        setQ(value);
        setSuggestOpen(false);
        const to = `/product?q=${encodeURIComponent(value)}`;
        navigate(to);
    };
    const onKeyDown = (e) => {
        if (!suggestOpen || suggest.length === 0) return;
        if (e.key === 'ArrowDown') {
            e.preventDefault();
            setActiveIdx((idx) => (idx + 1) % suggest.length);
        } else if (e.key === 'ArrowUp') {
            e.preventDefault();
            setActiveIdx((idx) => (idx <= 0 ? suggest.length - 1 : idx - 1));
        } else if (e.key === 'Enter') {
            if (activeIdx >= 0 && activeIdx < suggest.length) {
                e.preventDefault();
                onPickSuggestion(suggest[activeIdx]);
            }
        } else if (e.key === 'Escape') {
            setSuggestOpen(false);
        }
    };
    return (
        <nav className="navbar navbar-expand-lg navbar-light bg-light sticky-top py-2">
            <div className="container">
                {/* Brand with logo */}
                <button className="navbar-brand btn p-0 border-0 bg-transparent d-flex align-items-center" onClick={() => go('/')} aria-label="Go to homepage">
                    <i className="fa fa-bag-shopping text-primary me-2" aria-hidden="true"></i>
                    <span className="brand-dynamic">{brandName}</span>
                </button>

                <button className="navbar-toggler" type="button" data-bs-toggle="collapse" data-bs-target="#mainNav" aria-controls="mainNav" aria-expanded="false" aria-label="Toggle navigation">
                    <span className="navbar-toggler-icon"></span>
                </button>

                <div className="collapse navbar-collapse" id="mainNav">
                    {/* Left side: main nav */}
                    <ul className="navbar-nav me-auto mb-2 mb-lg-0 align-items-center">
                        {/* Categories dropdown */}
                        <li className="nav-item dropdown">
                            <button className="nav-link dropdown-toggle btn btn-link" id="catDropdown" data-bs-toggle="dropdown" aria-expanded="false">
                                Categories
                            </button>
                            <ul className="dropdown-menu" aria-labelledby="catDropdown">
                                <li><button className="dropdown-item" onClick={() => go('/product?cat=all')}>All</button></li>
                                <li className="dropdown-submenu position-relative">
                                    <button className="dropdown-item dropdown-toggle" data-bs-toggle="dropdown" aria-expanded="false" tabIndex="-1">Electronics</button>
                                    <ul className="dropdown-menu" style={{left:'100%',top:0,minWidth:'220px'}}>
                                        <li><button className="dropdown-item" onClick={()=>go('/product?cat=electronics&q=airpods')}>Airpods</button></li>
                                        <li><button className="dropdown-item" onClick={()=>go('/product?cat=electronics&q=handfree')}>Handfree</button></li>
                                        <li><button className="dropdown-item" onClick={()=>go('/product?cat=electronics&q=charger')}>Mobile Chargers & Adaptors</button></li>
                                        <li><button className="dropdown-item" onClick={()=>go('/product?cat=electronics&q=data cable')}>Data Cables</button></li>
                                    </ul>
                                </li>
                                <li className="dropdown-submenu position-relative">
                                    <button className="dropdown-item dropdown-toggle" data-bs-toggle="dropdown" aria-expanded="false" tabIndex="-1">Men's Clothing</button>
                                    <ul className="dropdown-menu" style={{left:'100%',top:0,minWidth:'180px'}}>
                                        <li><button className="dropdown-item" onClick={()=>go('/product?cat=men&q=winter')}>Winter</button></li>
                                        <li><button className="dropdown-item" onClick={()=>go('/product?cat=men&q=summer')}>Summer</button></li>
                                    </ul>
                                </li>
                                <li className="dropdown-submenu position-relative">
                                    <button className="dropdown-item dropdown-toggle" data-bs-toggle="dropdown" aria-expanded="false" tabIndex="-1">Women's Clothing</button>
                                    <ul className="dropdown-menu" style={{left:'100%',top:0,minWidth:'180px'}}>
                                        <li><button className="dropdown-item" onClick={()=>go('/product?cat=women&q=winter')}>Winter</button></li>
                                        <li><button className="dropdown-item" onClick={()=>go('/product?cat=women&q=summer')}>Summer</button></li>
                                    </ul>
                                </li>
                            </ul>
                        </li>
                        <li className="nav-item">
                            <button className="nav-link btn btn-link" onClick={() => go('/product?deals=1')}>Deals</button>
                        </li>
                        <li className="nav-item">
                            <button className="nav-link btn btn-link" onClick={() => go('/product?new=1')}>What's New</button>
                        </li>
                        <li className="nav-item">
                            <button className="nav-link btn btn-link" onClick={() => go('/delivery')}>Delivery</button>
                        </li>
                        <li className="nav-item">
                            <button className="nav-link btn btn-link" onClick={() => go('/about')}>About</button>
                        </li>
                    </ul>

                    {/* Right side: search + account + cart */}
                    <form className="d-flex header-search my-2 my-lg-0" onSubmit={onSearchSubmit} role="search" aria-label="Search products">
                        <div className="input-group input-group-sm position-relative">
                            <span className="input-group-text bg-transparent border-end-0"><i className="fa fa-search text-muted"></i></span>
                            <input
                                type="search"
                                ref={inputRef}
                                className="form-control border-start-0"
                                placeholder="Search products..."
                                value={q}
                                onChange={(e) => { setQ(e.target.value); setSuggestOpen(true); }}
                                onKeyDown={onKeyDown}
                                onFocus={() => { if (suggest.length) setSuggestOpen(true); }}
                                onBlur={() => setTimeout(() => setSuggestOpen(false), 120)}
                                aria-label="Search"
                            />
                            <button className="btn btn-dark d-none d-sm-inline-flex" type="submit">Search</button>
                            {suggestOpen && suggest.length > 0 && (
                                <ul className="search-suggest list-unstyled m-0 p-1 position-absolute top-100 start-0 end-0" role="listbox">
                                    {suggest.map((s, i) => (
                                        <li
                                            key={s + i}
                                            role="option"
                                            aria-selected={i === activeIdx}
                                            className={`suggest-item ${i === activeIdx ? 'active' : ''}`}
                                            onMouseDown={(e) => { e.preventDefault(); onPickSuggestion(s); }}
                                        >
                                            <i className="fa fa-search me-2 text-muted"></i>{s}
                                        </li>
                                    ))}
                                </ul>
                            )}
                        </div>
                    </form>

                    <ul className="navbar-nav ms-lg-3 align-items-lg-center">
                        {/* Theme toggle */}
                        <li className="nav-item me-lg-2 my-2 my-lg-0">
                            <button
                                className="btn btn-sm btn-outline-secondary d-flex align-items-center"
                                onClick={toggleTheme}
                                aria-label={dark ? 'Switch to light mode' : 'Switch to dark mode'}
                                title={dark ? 'Light mode' : 'Dark mode'}
                            >
                                {dark ? (
                                    <>
                                        <i className="fa fa-sun me-1"></i>
                                        Light
                                    </>
                                ) : (
                                    <>
                                        <i className="fa fa-moon me-1"></i>
                                        Dark
                                    </>
                                )}
                            </button>
                        </li>
                        {/* Account dropdown */}
                        <li className="nav-item dropdown">
                            <button className="nav-link dropdown-toggle btn btn-link" id="acctDropdown" data-bs-toggle="dropdown" aria-expanded="false">
                                <i className="fa fa-user me-1"></i> Account
                            </button>
                            <ul className="dropdown-menu dropdown-menu-end" aria-labelledby="acctDropdown">
                                <li><button className="dropdown-item" onClick={() => go('/login')}><i className="fa fa-sign-in-alt me-2"></i>Login</button></li>
                                <li><button className="dropdown-item" onClick={() => go('/register')}><i className="fa fa-user-plus me-2"></i>Register</button></li>
                            </ul>
                        </li>
                        <li className="nav-item ms-lg-2 my-2 my-lg-0">
                            <button className="btn btn-dark position-relative" onClick={() => go('/cart')}>
                                <i className="fa fa-cart-shopping me-2"></i>
                                Cart
                                <span className="position-absolute top-0 start-100 translate-middle badge rounded-pill bg-danger">
                                    {state.length}
                                </span>
                            </button>
                        </li>
                    </ul>
                </div>
            </div>
        </nav>
    )
}

export default Navbar